import { useCallback, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import { ArrowRight, Eye, EyeOff } from '@opengov/react-capital-assets';

import { useAppNavigation } from '../hooks/useAppNavigation';

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function LoginPage() {
  const navigate = useNavigate();
  const { buildPath } = useAppNavigation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      setLoading(true);
      setTimeout(() => navigate(buildPath('')), 800);
    },
    [navigate, buildPath],
  );

  const year = new Date().getFullYear();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Left side — branding + login */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          position: 'relative',
        }}
      >
        {/* Logo + Title */}
        <Box sx={{ px: 4, pt: 4, pb: 1 }}>
          {/* Shield icon */}
          <Box
            aria-hidden="true"
            sx={{
              width: 72,
              height: 72,
              borderRadius: 3,
              bgcolor: (t) => t.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <Box
              component="svg"
              viewBox="0 0 24 24"
              sx={{ width: 40, height: 40, fill: 'white' }}
            >
              <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 4l5 2.18V11c0 3.5-2.33 6.79-5 7.93-2.67-1.14-5-4.43-5-7.93V7.18L12 5zm-1 3v4l-2-2-1.41 1.41L11 14.83l1 1 1-1 4.41-4.42L16 9l-2 2V8h-3z" />
            </Box>
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
            Veterans Case Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Veterans Services Office
          </Typography>
        </Box>

        {/* Login card */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', px: { xs: 3, sm: 6 } }}>
          <Card sx={{ width: '100%', maxWidth: 420, p: { xs: 4, sm: 5 } }} elevation={2}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Welcome back
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Sign in to the Veterans Case Management platform
            </Typography>

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2.5}>
                <TextField
                  label="Email"
                  type="email"
                  autoComplete="email"
                  required
                  fullWidth
                  size="small"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@veterans.gov"
                />
                <TextField
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  fullWidth
                  size="small"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            tabIndex={-1}
                          >
                            {showPassword ? <EyeOff /> : <Eye />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <FormControlLabel
                    control={<Checkbox size="small" />}
                    label={<Typography variant="caption">Remember me</Typography>}
                  />
                  <Link
                    component="button"
                    type="button"
                    variant="caption"
                    underline="hover"
                    color="text.secondary"
                  >
                    Forgot password?
                  </Link>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <ArrowRight />}
                  sx={{ py: 1.25 }}
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </Stack>
            </Box>
          </Card>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 4,
            py: 2.5,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            &copy; {year} Veterans Services Office
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: { xs: 'none', sm: 'inline' } }}
          >
            |
          </Typography>
          <Link href="#" variant="caption" underline="hover" color="text.secondary">
            Terms of Use
          </Link>
          <Link href="#" variant="caption" underline="hover" color="text.secondary">
            Privacy Policy
          </Link>
        </Box>
      </Box>

      {/* Right side — branded panel (hidden on mobile) */}
      <Box
        sx={{
          display: { xs: 'none', lg: 'flex' },
          width: '50%',
          p: 3,
          alignSelf: 'stretch',
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: 6,
            background: (t) =>
              `linear-gradient(145deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 60%, #6B60FF 100%)`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 6,
            gap: 4,
            color: 'white',
          }}
        >
          {/* Large shield emblem */}
          <Box
            component="svg"
            viewBox="0 0 24 24"
            aria-hidden="true"
            sx={{ width: 96, height: 96, fill: 'rgba(255,255,255,0.15)' }}
          >
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
          </Box>

          <Box sx={{ textAlign: 'center', maxWidth: 360 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, color: 'white', mb: 2, lineHeight: 1.2 }}>
              Serving Those Who Served
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.7 }}>
              A unified platform to manage veteran cases, coordinate services, and ensure every veteran
              receives the benefits and support they've earned.
            </Typography>
          </Box>

          <Box
            sx={{
              borderTop: '1px solid rgba(255,255,255,0.2)',
              pt: 3,
              width: '100%',
              maxWidth: 360,
              textAlign: 'center',
            }}
          >
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              Veterans Services Office &middot; Case Management System
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
